export interface ProductBlockHeadingProps {
  /** El rótulo pequeño en monoespaciada. Cadena de INTERFAZ, ya traducida. */
  eyebrow: string;
  /** "01", "02"… Se calcula sobre los bloques que EXISTEN, no está fijo. */
  number?: string;
  /** El título del bloque. Viene del catálogo, no de las traducciones. */
  title?: string;
  /** La entradilla del bloque, también del catálogo. */
  intro?: string;
  /**
   * `split` pone el título a la izquierda y la entradilla a la derecha, en
   * dos columnas. Es la cabecera de los pilares en la ficha original y
   * necesita una entradilla larga para no verse coja; con textos cortos,
   * `stacked` se lee mejor.
   */
  layout?: "stacked" | "split";
  /** `dark` cuando el bloque se pinta sobre fondo oscuro. */
  tone?: "light" | "dark";
}

/**
 * Cierra el título con punto si no termina ya en signo.
 *
 * Es el tic editorial de la ficha original: "La Biología del
 * Envejecimiento Celular." — la afirmación se cierra. Se comprueba antes
 * de añadirlo porque el título lo escribe quien administra el catálogo y
 * bien puede venir con su propio punto, o con una interrogación; "¿Qué
 * es?.." se lee como un error de programación, que es lo que sería.
 */
export function withClosingPeriod(title: string): string {
  return /[.!?…:]$/.test(title.trim()) ? title : `${title}.`;
}

/**
 * La cabecera de cualquier bloque editorial: rótulo numerado, título y
 * entradilla.
 *
 * ── Por qué el rótulo va numerado y el número no está escrito ────────
 * La ficha original numera sus bloques a mano —"04 / Pilares"— y eso
 * funciona mientras TODOS los bloques existan. Aquí no existen: un
 * producto puede traer solo cifras y cierre. Con los números escritos a
 * mano, esa ficha empezaría en el 07 y quien la lea buscaría los seis
 * anteriores. El número lo calcula `ProductDetail` sobre los bloques que
 * de verdad se van a pintar, y por eso entra como prop.
 *
 * ── Ni serif itálica ni cápsula ──────────────────────────────────────
 * El rótulo es texto plano en monoespaciada, con su tamaño y su
 * espaciado haciendo el trabajo. Las píldoras de rótulo salieron del
 * sistema, y la cursiva de la fuente display también.
 *
 * ── El color del rótulo NO es `ds-ink-4` ─────────────────────────────
 * La ficha original lo pinta en `--ink-4` (#8A8377), que sobre crema se
 * queda en 2.97:1 — por debajo del 4.5:1 que exige un texto de 11 px.
 * Aquí va en `ds-ink-3` (#3A2F55), que da 12.2:1 sobre blanco y sigue
 * leyéndose como un rótulo secundario por el tamaño y el espaciado.
 */
export function ProductBlockHeading({
  eyebrow,
  number,
  title,
  intro,
  layout = "stacked",
  tone = "light",
}: ProductBlockHeadingProps) {
  const dark = tone === "dark";
  const label = (
    <p
      className={
        dark
          ? "inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 font-mono text-[10.5px] uppercase leading-none tracking-[0.18em] text-white/80"
          : "inline-flex w-fit items-center gap-2 rounded-full border border-ds-border bg-white px-3 py-1.5 font-mono text-[10.5px] uppercase leading-none tracking-[0.18em] text-ds-ink-3 shadow-ds-xs"
      }
    >
      {number ? `${number} / ` : ""}
      {eyebrow}
    </p>
  );

  const heading = title ? (
    <h2 className={`text-[clamp(28px,3.5vw,40px)] font-light leading-[1.1] tracking-[-0.025em] ${dark ? "text-white" : "text-ds-ink"}`}>
      {withClosingPeriod(title)}
    </h2>
  ) : null;

  const lead = intro ? (
    <p className={`max-w-prose text-[15px] font-light leading-[1.65] ${dark ? "text-white/75" : "text-ds-ink-3"}`}>
      {intro}
    </p>
  ) : null;

  if (layout === "split" && heading && lead) {
    return (
      <div className="grid gap-4">
        {label}
        <div className="grid gap-6 lg:grid-cols-2 lg:gap-10">
          {heading}
          {lead}
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      {label}
      {heading}
      {lead}
    </div>
  );
}
